import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useClearRoomControllerMutation } from "hooks/mutations/useClearRoomControllerMutation";
import { useCompleteVisitMutation } from "hooks/mutations/useCompleteVisitMutation";
import { useShowNekoLens } from "hooks/useShowNekoLens";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback } from "react";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { List } from "render/ui/presentation/List";
import { Link } from "render/ui/trigger/Link";
import styles from "../CheckInCompleteView/styles.module.sass";

type FollowUpDermaViewProps = {
  goTo(url: string): void;
} & CheckInParams;

export function FollowUpDermaView({
  goTo,
  patientId,
  visitId,
}: FollowUpDermaViewProps) {
  const clearRoomController = useClearRoomControllerMutation();
  const completeVisit = useCompleteVisitMutation();
  const showNekoLens = useShowNekoLens({ patientId, visitId });

  const goToNekoLensAuth = useCallback(() => {
    const url = paths.nekoLensAuth.url({ patientId, visitId });
    goTo(url);
  }, [goTo, patientId, visitId]);

  const goToHome = useCallback(async () => {
    try {
      await Promise.all([
        clearRoomController.mutateAsync({ patientId, visitId }),
        completeVisit.mutateAsync({ patientId, visitId }),
      ]);
      goTo(paths.root.url({}));
    } catch {
      // errors surface via useReporting.logError in each hook; stay on screen
    }
  }, [clearRoomController, completeVisit, goTo, patientId, visitId]);

  const isCheckingOut =
    clearRoomController.isPending || completeVisit.isPending;

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            {showNekoLens ? (
              <>
                <Typography variant="display-s">Follow-up exams</Typography>
                <List>
                  <List.Item>
                    <div className={styles.scanItem}>
                      <div className={styles.itemTitle}>
                        <Typography variant="title-s">Derma</Typography>
                        <div className={styles.itemActions}>
                          <Link onPress={goToNekoLensAuth}>Show QR code</Link>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                </List>
                <div className={styles.divider} />
              </>
            ) : undefined}
            <section className={styles.checkoutSection}>
              <div className={styles.checkoutText}>
                <Typography variant="title-l">Ready to check out?</Typography>
                <Typography variant="body-m" color="subtle">
                  Check out if all examinations are complete
                </Typography>
              </div>
              <Button
                onPress={goToHome}
                fillContainer
                icon={<Icon24ArrowRight />}
                state={isCheckingOut ? "loading" : undefined}
              >
                Check Out
              </Button>
            </section>
          </section>
        }
      />
    </PageFrameContent>
  );
}
